/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type { WalletPassInstall } from "@app/server/trpc/routers/wallet-passes";
import { WalletBadges } from "../CollectedPage/WalletBadges";
import { PassPreview } from "./PassPreview";

/**
 * /wallet/:serial — where the wallet emails send a customer to add their card.
 * No sign-in: the serial is the credential, as on the download routes the
 * badges link to. Each badge downloads the pass for its wallet: Apple's
 * .pkpass (iOS opens the add sheet) or Google's save link.
 */
export const WalletPassInstallPage = defineComponent({
  name: "WalletPassInstallPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const route = useRoute();

    const pass = ref<WalletPassInstall | null>(null);
    const state = ref<"loading" | "ready" | "not_found" | "error">("loading");

    onMounted(async () => {
      try {
        pass.value = await trpc.walletPasses.install.query({
          serial: String(route.params.serial ?? ""),
        });
        state.value = "ready";
      } catch (error) {
        const code = (error as { data?: { code?: string } })?.data?.code;
        // A malformed serial fails input validation; treat it as unknown.
        state.value =
          code === "NOT_FOUND" || code === "BAD_REQUEST"
            ? "not_found"
            : "error";
      }
    });

    const message = (text: string) => (
      <div class="rounded-2xl border border-base-300 bg-base-100 p-6 text-center text-sm text-base-content/70">
        {text}
      </div>
    );

    const content = (current: WalletPassInstall) => {
      const serial = encodeURIComponent(current.serial);
      const available = current.apple || current.google;
      return (
        <div class="space-y-6">
          <PassPreview
            kind={current.kind}
            programName={current.program_name}
            placeName={current.place_name}
            design={current.design}
          />
          <div class="text-center">
            <h1 class="text-2xl font-bold text-base-content">
              {t("customer.wallet_install.title")}
            </h1>
            <p class="mt-2 text-sm text-base-content/70">
              {t("customer.wallet_install.subtitle", [current.program_name])}
            </p>
          </div>
          {available ? (
            <WalletBadges
              large
              appleUrl={
                current.apple
                  ? `/api/v3/customer/wallet/pass/${serial}.pkpass`
                  : null
              }
              googleUrl={
                current.google
                  ? `/api/v3/customer/wallet/google/${serial}`
                  : null
              }
            />
          ) : (
            message(t("customer.wallet_install.unavailable"))
          )}
          <p class="text-center text-xs text-base-content/50">
            {t("customer.wallet_install.updates_hint")}
          </p>
        </div>
      );
    };

    return () => (
      <div class="min-h-screen bg-base-200">
        <div class="mx-auto w-full max-w-sm px-4 py-10">
          {state.value === "loading" ? (
            <div class="py-24 text-center">
              <span class="loading loading-spinner loading-lg text-primary" />
            </div>
          ) : state.value === "not_found" ? (
            message(t("customer.wallet_install.not_found"))
          ) : state.value === "error" || !pass.value ? (
            <div class="alert alert-error text-sm">
              {t("customer.wallet_install.load_error")}
            </div>
          ) : (
            content(pass.value)
          )}
        </div>
      </div>
    );
  },
});
